/* eslint-disable sonarjs/no-identical-functions */
import React, { useEffect, useState } from 'react';

import { nanoid } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import { bemCssModules } from '@utils';

import {
  loginActions,
  userActions,
  useStore,
  withdrawActions,
  sellActions,
  globalActions,
  accountsActions,
} from '@modules';

import Captcha from '@components/Captcha';
import ResendCode from '@components/ResendCode';
import VerifyCode from '@components/VerifyCode';

import style from './Verification.scss';

const cn = bemCssModules(style, 'Verification');

export type VerificationType = {
  type: 'phone' | 'email' | '2fa';
  isLoading: boolean;
  onResend?(): void;
  onVerify(code: string, key?: string, captcha?: string): void;
  tooltip?: React.ReactNode;
  id?: string;
  value?: string;
  codeLength?: number;
  customInputClass?: string;
  error?: string;
  description?: string;
  isWithdraw?: boolean;
  isAccountsWithdraw?: boolean;
  hideResend?: boolean;
};

const Verification: CustomFC<VerificationType> = props => {
  const {
    className,
    children,
    type,
    isLoading,
    onResend,
    onVerify,
    tooltip,
    id,
    value,
    customInputClass,
    error,
    codeLength,
    description,
    hideResend = false,
    isWithdraw,
    isAccountsWithdraw,
  } = props;

  const { t } = useTranslation();
  const [{ login, user, global, withdraw, sell, accounts }, dispatch] = useStore();
  const { verification } = login;
  const { isShowCaptcha, captcha } = global;
  const validationError =
    error ||
    withdraw.errors.data?.code?.[0] ||
    sell.errors.data?.code?.[0] ||
    user.errors.data?.new_password?.[0] ||
    user.errors.data?.code?.[0] ||
    login.errors.data?.code?.[0] ||
    '';

  const { resendVerification } = login.loaders;
  const { withdrawResendVerification } = withdraw.loaders;
  const { accountWithdrawVerifyResend } = accounts.loaders;

  const timeout = verification?.timeout;
  const code_length = codeLength || verification?.code_length;
  const targetValue = value || verification?.[type] || user.data?.[type] || '';
  const key = verification?.key;

  const [resendButtonId] = useState(nanoid());
  const [code, setCode] = useState('');

  const handleSubmit = (codeValue: string) => {
    onVerify(codeValue, key, captcha);
  };

  const clearErrors = () => {
    if (validationError) {
      dispatch(userActions.clearErrors());
      dispatch(loginActions.clearErrors());
      dispatch(withdrawActions.clearErrors());
      dispatch(sellActions.clearErrors());
    }
  };

  useEffect(() => {
    return () => {
      clearErrors();
    };
  }, []);

  const handleResendCode = () => {
    if (!key) return;
    if (isShowCaptcha) dispatch(globalActions.setShowCaptcha(false));

    if (isAccountsWithdraw) dispatch(accountsActions.accountWithdrawVerifyResend({ key, captcha }));
    else if (isWithdraw) dispatch(withdrawActions.withdrawResendVerification({ key, captcha }));
    else dispatch(loginActions.resendVerification({ key, captcha }));

    if (typeof onResend === 'function') onResend();
  };

  const isDisabled = isLoading || (isShowCaptcha && !captcha.length);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setCode(inputValue);
    if (inputValue.length === code_length) handleSubmit(inputValue);
    clearErrors();
  };

  const isFetching =
    !!accountWithdrawVerifyResend || !!withdrawResendVerification || !!resendVerification;

  const resendIsHidden = isAccountsWithdraw && type === 'phone';

  return (
    <div className={cn(null, [className])}>
      <h1 className={cn('title')}>{t(`public.${type}_code`)}</h1>

      <VerifyCode
        code={code}
        targetValue={targetValue}
        codeType={type}
        codeLength={code_length}
        onChange={handleChange}
        isLoading={isLoading}
        customInputClass={customInputClass}
        error={validationError}
        tooltip={tooltip}
        description={description}
      />

      {!resendIsHidden && !hideResend && (
        <ResendCode
          id={id || resendButtonId}
          className={cn('resend', { isTooltip: !!tooltip, error: !!validationError })}
          timeout={timeout}
          onResend={handleResendCode}
          isLoading={isFetching}
          disabled={isDisabled}
        />
      )}
      {children}
      {isShowCaptcha && <Captcha className={cn('captcha')} />}
    </div>
  );
};
export default Verification;
